import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { money, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash, CaretDown, CaretRight } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CustomerPicker from "@/components/CustomerPicker";
import DateInput from "@/components/DateInput";

const TABS = [
  { value: "all", label: "All" },
  { value: "unpaid", label: "Unpaid" },
  { value: "partial", label: "Partial" },
  { value: "paid", label: "Paid" },
  { value: "archived", label: "Archived" },
  { value: "voided", label: "Voided" },
];

const emptyItem = () => ({
  product_id: null,
  name: "",
  color: "",
  size: "",
  quantity: 1,
  unit_price: "",
  colors: [], // available options from picked product
  sizes: [],
});

const emptySpecs = () => ({
  deadline: "",
  artwork_notes: "",
  material: "",
  material_source: "shop",
  color: "",
  thickness: "",
  dimensions: "",
});

export default function Invoices() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [jobType, setJobType] = useState("laser");
  const [items, setItems] = useState([emptyItem()]);
  const [specs, setSpecs] = useState(emptySpecs());
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [tab, setTab] = useState("all");

  const products = useMemo(() => catalog.filter((c) => (c.type || "product") !== "material"), [catalog]);
  const materials = useMemo(() => catalog.filter((c) => c.type === "material"), [catalog]);

  const load = () => {
    let url = "/invoices";
    if (tab === "archived") url = "/invoices?archived=true";
    else if (tab === "voided") url = "/invoices?voided=true";
    return api.get(url).then((r) => setList(r.data));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/products").then((r) => setCatalog(r.data));
  }, []);

  useEffect(() => {
    const c = params.get("customer");
    const isNew = params.get("new");
    if (isNew && c) { setCustomerId(c); setOpen(true); }
  }, [params]);

  const pickProduct = (idx, pid) => {
    const p = products.find((x) => x.id === pid);
    if (!p) return;
    const next = [...items];
    next[idx] = {
      ...next[idx],
      product_id: p.id,
      name: p.name,
      unit_price: p.default_price ?? "",
      colors: p.variations?.colors || [],
      sizes: p.variations?.sizes || [],
      color: "",
      size: "",
    };
    setItems(next);
  };

  const updateItem = (idx, key, value) => {
    const next = [...items];
    next[idx] = { ...next[idx], [key]: value };
    setItems(next);
  };
  const addLine = () => setItems([...items, emptyItem()]);
  const removeLine = (idx) => setItems(items.filter((_, i) => i !== idx));

  const applyMaterial = (mid) => {
    const m = materials.find((x) => x.id === mid);
    if (!m) return;
    setSpecs((s) => ({
      ...s,
      material: m.name,
      color: m.color || s.color,
      thickness: m.thickness || s.thickness,
    }));
  };

  const reset = () => {
    setCustomerId(""); setJobType("laser");
    setItems([emptyItem()]);
    setSpecs(emptySpecs());
    setAdvancedOpen(false);
  };

  const create = async () => {
    if (!customerId) { toast.error("Choose a customer"); return; }
    const cleanItems = items
      .filter((it) => (it.name || "").trim() || Number(it.unit_price) > 0)
      .map((it) => {
        const parts = [it.name, it.color, it.size].filter(Boolean);
        return {
          description: parts.join(" · "),
          quantity: Number(it.quantity) || 1,
          unit_price: Number(it.unit_price) || 0,
          product_id: it.product_id || null,
        };
      });

    if (cleanItems.length === 0) {
      toast.error("Add at least one line item");
      return;
    }

    // Total quantity for the WO
    const totalQty = cleanItems.reduce((n, i) => n + (Number(i.quantity) || 1), 0);
    // Best-effort spec fill: use first item's variation values if no advanced specs entered
    const first = items.find((it) => (it.name || "").trim()) || items[0];
    const finalColor = specs.color || first?.color || "";
    const finalSize = specs.dimensions || first?.size || "";

    const settings = await api.get("/settings").then((r) => r.data);

    const { data } = await api.post("/invoices", {
      customer_id: customerId,
      items: cleanItems,
      tax_rate: settings.default_tax_rate || 0,
      job_type: jobType,
      wo_material: specs.material,
      wo_dimensions: finalSize,
      wo_color: finalColor,
      wo_thickness: specs.thickness,
      wo_material_source: specs.material_source,
      wo_quantity: totalQty,
      wo_artwork_notes: specs.artwork_notes,
      wo_deadline: specs.deadline || null,
    });
    toast.success(`Created ${data.number} + companion work order`);
    setOpen(false); reset();
    navigate(`/invoices/${data.id}`);
  };

  const filtered = (tab === "all" || tab === "archived" || tab === "voided") ? list : list.filter((i) => i.payment_status === tab);

  return (
    <div>
      <PageHeader
        title="Invoices"
        subtitle={`${list.length} total`}
        actions={
          <button className="btn-primary flex items-center gap-2" onClick={() => setOpen(true)} data-testid="new-invoice-btn">
            <Plus size={16} /> New Invoice
          </button>
        }
      />

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList className="bg-transparent border hairline p-1 rounded-sm h-auto">
          {TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value} data-testid={`inv-tab-${t.value}`}
              className="rounded-sm text-xs uppercase tracking-wider font-mono data-[state=active]:bg-emerald-700 data-[state=active]:text-white">
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="surface overflow-x-auto fade-up" data-testid="invoice-list">
        <table className="data-table">
          <thead>
            <tr><th>Number</th><th>Customer</th><th>Issued</th><th>Total</th><th>Paid</th><th>Status</th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center text-slate-400 py-10">No invoices in this view.</td></tr>
            )}
            {filtered.map((i) => (
              <tr key={i.id} onClick={() => navigate(`/invoices/${i.id}`)} data-testid={`inv-row-${i.id}`}>
                <td className="font-mono text-slate-900 font-medium">{i.number}</td>
                <td>{i.customer_name || "—"}</td>
                <td className="font-mono">{fmtDate(i.created_at)}</td>
                <td className="font-mono">{money(i.total)}</td>
                <td className="font-mono">{money(i.amount_paid)}</td>
                <td><span className={`chip ${i.payment_status}`}>{i.payment_status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Invoice</DialogTitle></DialogHeader>

          <div className="py-2 space-y-6">
            {/* Section 1 — Customer & Job */}
            <section>
              <div className="overline mb-3">1 · Customer & Job</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <Label className="text-xs text-slate-600">Customer *</Label>
                  <CustomerPicker
                    customers={customers}
                    value={customerId}
                    onChange={setCustomerId}
                    onCustomersChange={setCustomers}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Job Type</Label>
                  <Select value={jobType} onValueChange={setJobType}>
                    <SelectTrigger data-testid="new-inv-job-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="laser">Laser Engraving</SelectItem>
                      <SelectItem value="uv">UV Flatbed Printing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            {/* Section 2 — Line Items */}
            <section>
              <div className="overline mb-3">2 · Line Items</div>
              <div className="flex flex-col gap-3">
                {items.map((it, idx) => (
                  <div key={idx} className="border hairline rounded-sm p-3 space-y-2" data-testid={`inv-line-${idx}`}>
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-12 md:col-span-5">
                        <Label className="text-[11px] text-slate-500">Item</Label>
                        <div className="flex gap-1">
                          <Select
                            value={it.product_id || ""}
                            onValueChange={(v) => pickProduct(idx, v)}
                          >
                            <SelectTrigger className="flex-1 min-w-0" data-testid={`inv-line-picker-${idx}`}>
                              <SelectValue placeholder={products.length ? "Pick from Catalog…" : "No products yet"} />
                            </SelectTrigger>
                            <SelectContent>
                              {products.length === 0 && (
                                <div className="p-2 text-xs text-slate-500">Add items in Catalog to reuse them here.</div>
                              )}
                              {products.map((p) => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.name}
                                  {p.type === "service" ? " · service" : ""}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Input
                          className="mt-1"
                          data-testid={`inv-line-name-${idx}`}
                          value={it.name}
                          onChange={(e) => updateItem(idx, "name", e.target.value)}
                          placeholder="…or type a custom item name"
                        />
                      </div>

                      {it.colors?.length > 0 && (
                        <div className="col-span-6 md:col-span-2">
                          <Label className="text-[11px] text-slate-500">Color</Label>
                          <Select value={it.color || ""} onValueChange={(v) => updateItem(idx, "color", v)}>
                            <SelectTrigger data-testid={`inv-line-color-${idx}`}><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>
                              {it.colors.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {it.sizes?.length > 0 && (
                        <div className="col-span-6 md:col-span-2">
                          <Label className="text-[11px] text-slate-500">Size</Label>
                          <Select value={it.size || ""} onValueChange={(v) => updateItem(idx, "size", v)}>
                            <SelectTrigger data-testid={`inv-line-size-${idx}`}><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>
                              {it.sizes.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      <div className="col-span-4 md:col-span-1">
                        <Label className="text-[11px] text-slate-500">Qty</Label>
                        <Input type="number" min="1" data-testid={`inv-line-qty-${idx}`}
                          value={it.quantity}
                          onChange={(e) => updateItem(idx, "quantity", e.target.value)} />
                      </div>
                      <div className="col-span-6 md:col-span-2">
                        <Label className="text-[11px] text-slate-500">Price ($)</Label>
                        <Input type="number" step="0.01" min="0" data-testid={`inv-line-price-${idx}`}
                          value={it.unit_price}
                          onChange={(e) => updateItem(idx, "unit_price", e.target.value)}
                          placeholder="0.00" />
                      </div>
                      <div className="col-span-2 md:col-span-12 md:flex md:justify-end">
                        {items.length > 1 && (
                          <button
                            className="text-slate-400 hover:text-red-400 p-1"
                            onClick={() => removeLine(idx)}
                            data-testid={`inv-line-remove-${idx}`}
                            title="Remove line"
                          >
                            <Trash size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <button className="btn-ghost self-start flex items-center gap-2" onClick={addLine} data-testid="inv-add-line">
                  <Plus size={14} /> Add line
                </button>
              </div>
            </section>

            {/* Section 3 — Job Details */}
            <section>
              <div className="overline mb-3">3 · Job Details</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600">Deadline</Label>
                  <DateInput data-testid="new-inv-deadline" value={specs.deadline}
                    onChange={(e) => setSpecs({ ...specs, deadline: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Material Provided By</Label>
                  <Select value={specs.material_source} onValueChange={(v) => setSpecs({ ...specs, material_source: v })}>
                    <SelectTrigger data-testid="new-inv-material-source"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="shop">Shop</SelectItem>
                      <SelectItem value="customer">Customer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs text-slate-600">Artwork / Design Notes</Label>
                  <Textarea data-testid="new-inv-artwork-notes" rows={2} value={specs.artwork_notes}
                    onChange={(e) => setSpecs({ ...specs, artwork_notes: e.target.value })}
                    placeholder="Font, alignment, engrave depth, print colors, etc." />
                </div>
              </div>

              {/* Advanced specs (collapsed by default) */}
              <button
                type="button"
                className="mt-3 flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900"
                onClick={() => setAdvancedOpen((v) => !v)}
                data-testid="inv-advanced-toggle"
              >
                {advancedOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
                Advanced material specs
              </button>
              {advancedOpen && (
                <div className="mt-3 border hairline p-3 rounded-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] text-slate-500">Overrides item variations on the Work Order.</div>
                    <Select value="" onValueChange={applyMaterial}>
                      <SelectTrigger className="w-56 h-8" data-testid="new-inv-material-picker">
                        <SelectValue placeholder={materials.length ? "Pick from Materials…" : "No materials yet"} />
                      </SelectTrigger>
                      <SelectContent>
                        {materials.length === 0 && (
                          <div className="p-2 text-xs text-slate-500">Add materials in Catalog → Materials.</div>
                        )}
                        {materials
                          .filter((m) => m.category === "both" || m.category === jobType)
                          .map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                              {m.name}{m.thickness ? ` · ${m.thickness}` : ""}{m.color ? ` · ${m.color}` : ""}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-600">Material</Label>
                      <Input data-testid="new-inv-material" value={specs.material}
                        onChange={(e) => setSpecs({ ...specs, material: e.target.value })}
                        placeholder="Wood, Acrylic, Aluminum…" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">Color</Label>
                      <Input data-testid="new-inv-color" value={specs.color}
                        onChange={(e) => setSpecs({ ...specs, color: e.target.value })}
                        placeholder="Black, Walnut, Clear…" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">Size / Dimensions</Label>
                      <Input data-testid="new-inv-dimensions" value={specs.dimensions}
                        onChange={(e) => setSpecs({ ...specs, dimensions: e.target.value })}
                        placeholder='12" x 6"' />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">Thickness</Label>
                      <Input data-testid="new-inv-thickness" value={specs.thickness}
                        onChange={(e) => setSpecs({ ...specs, thickness: e.target.value })}
                        placeholder='1/4", 3mm…' />
                    </div>
                  </div>
                </div>
              )}
            </section>

            <p className="text-xs text-slate-400">
              A matching work order is auto-created and linked — employees see all these details on the printable Work Order PDF.
            </p>
          </div>

          <DialogFooter>
            <button className="btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={create} data-testid="create-invoice-btn">Create Invoice</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
